import { useState, useEffect, useRef } from "react";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { PauseIcon } from "@phosphor-icons/react/dist/csr/Pause";
import { PlayIcon } from "@phosphor-icons/react/dist/csr/Play";
import { StopIcon } from "@phosphor-icons/react/dist/csr/Stop";

interface GlobalSettingsPanelProps {
  isPlaying: boolean;
  onPlay: () => void; onPause: () => void; onStop: () => void;
  onSeek: (time: number) => void;
  getCurrentTime: () => number; getDuration: () => number;
  onBackToUpload: () => void;
  audioSourceLabel?: string;
  isSystemAudio?: boolean;
  isMicrophone?: boolean;
}

function formatTime(seconds: number) {
  const time = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  return Math.floor(time / 60).toString().padStart(2, "0") + ":" + Math.floor(time % 60).toString().padStart(2, "0");
}

export function GlobalSettingsPanel({ isPlaying, onPlay, onPause, onStop, onSeek,
  getCurrentTime, getDuration, onBackToUpload, audioSourceLabel, isSystemAudio = false, isMicrophone = false }: GlobalSettingsPanelProps) {
  const [displayTime, setDisplayTime] = useState(() => getCurrentTime());
  const [duration, setDuration] = useState(() => getDuration());
  const seeking = useRef(false);
  const seekValue = useRef(0);

  useEffect(() => {
    if (isSystemAudio) return;
    const update = () => {
      setDuration(getDuration());
      if (!seeking.current) setDisplayTime(getCurrentTime());
    };
    update();
    const timer = window.setInterval(update, 100);
    return () => window.clearInterval(timer);
  }, [getCurrentTime, getDuration, isSystemAudio]);

  const finishSeek = () => {
    if (!seeking.current) return;
    seeking.current = false;
    onSeek(seekValue.current);
    setDisplayTime(seekValue.current);
  };
  const cleanLabel = (audioSourceLabel || "Audio source").replace(/_spotdown\.org/gi, "").replace(/\.(mp3|wav|flac|m4a|aac|ogg|aiff?)$/i, "");
  const [title, ...subtitle] = cleanLabel.split(/\s[-–—]\s/);

  return <section className="instrument-section audio-section" aria-labelledby="audio-heading">
    <h2 id="audio-heading" className="instrument-heading ruled-heading">AUDIO</h2>
    <div className="audio-track-row">
      <div className="audio-track" title={audioSourceLabel}>
        <h3>{title}</h3>
        <p>{isSystemAudio ? (isMicrophone ? "MICROPHONE / LIVE" : "SYSTEM AUDIO / LIVE") : subtitle.join(" — ") || "LOCAL AUDIO"}</p>
      </div>
      <button className="text-button change-audio" onClick={onBackToUpload}
        aria-label={isSystemAudio ? "停止并更换音源" : "更换音频"}>
        <span>{isSystemAudio ? "更换音源" : "更换音频"}</span><ArrowRightIcon size={16} />
      </button>
    </div>
    {!isSystemAudio && <>
      <input className="transport-seek" aria-label="播放进度" type="range" min={0} max={duration || 1} step={0.1}
        value={Math.min(displayTime, duration || 1)} disabled={duration <= 0}
        onPointerDown={event => {
          seeking.current = true; seekValue.current = displayTime;
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onChange={event => {
          const value = event.currentTarget.valueAsNumber;
          seekValue.current = value; setDisplayTime(value);
          if (!seeking.current) onSeek(value);
        }}
        onPointerUp={finishSeek}
        onPointerCancel={() => { seeking.current = false; setDisplayTime(getCurrentTime()); }}
        onBlur={finishSeek} />
      <div className="transport-time"><span>{formatTime(displayTime)}</span><span>{formatTime(duration)}</span></div>
      <div className="transport-buttons">
        <button onClick={isPlaying ? onPause : onPlay} aria-label={isPlaying ? "Pause" : "Play"} title="Space · 播放 / 暂停">
          {isPlaying ? <PauseIcon size={18} weight="fill" /> : <PlayIcon size={18} weight="fill" />}
          <span>{isPlaying ? "PAUSE" : "PLAY"}</span>
        </button>
        <button onClick={onStop} aria-label="Stop"><StopIcon size={18} weight="fill" /><span>STOP</span></button>
      </div>
    </>}
    {isSystemAudio && <>
      <p className="source-live-note">{isPlaying ? (isMicrophone ? "正在收音 · 不回放声音" : "正在接收系统音频") : "输入已暂停"}</p>
      <div className="transport-buttons">
        <button onClick={isPlaying ? onPause : onPlay}>{isPlaying ? "暂停响应" : "恢复响应"}</button>
        <button onClick={onBackToUpload}>断开音源</button>
      </div>
    </>}
  </section>;
}
